import React, { createContext, useContext, useState, useCallback } from "react";
import { useBotConfiguration } from "../hooks/useBotConfiguration";
import { useBotConfigurationChannel } from "./WebSocketProvider";
import { AuthContext } from "./AuthProvider";
import { useAssistantContext } from "../contexts/AssistantContext";

const BotConfigurationContext = createContext();

export const useBotConfigurationContext = () => {
  const context = useContext(BotConfigurationContext);
  if (!context) {
    throw new Error('useBotConfigurationContext deve ser usado dentro de BotConfigurationProvider');
  }
  return context;
};

/**
 * BotConfigurationProvider
 * Usando WebSocketProvider centralizado
 *
 * Benefícios:
 * - Não gerencia conexão WebSocket diretamente
 * - Usa hook centralizado para subscrição
 * - Lógica mais limpa e focada apenas em configuração do bot
 * - Melhor separação de responsabilidades
 */
export const BotConfigurationProvider = ({ children }) => {
  const botConfig = useBotConfiguration();
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const { user } = useContext(AuthContext);
  const { selectedAssistant } = useAssistantContext();

  // Extract partnerId from user - only for partner users
  const partnerId = (user?.userType === 'partner' || user?.userType === 'partnerUser')
    ? (user?.partnerId || user?.id || null)
    : null;

  // Extract assistantId from selected assistant
  const assistantId = selectedAssistant?.id || null;

  // Função para forçar atualização do estado com delay opcional
  const forceRefresh = useCallback((immediate = false) => {
    if (immediate) {
      botConfig.refreshConfiguration();
    } else {
      // Adicionar delay para evitar race condition com o backend
      setTimeout(() => {
        botConfig.refreshConfiguration();
      }, 300);
    }
    setLastUpdate(Date.now());
  }, [botConfig]);

  // Event handlers para o canal de Bot Configuration
  const botChannelHandlers = React.useMemo(() => ({

    onConfigurationUpdated: (payload) => {
      if (payload && botConfig.updateFromWebSocket) {
        botConfig.updateFromWebSocket(payload);
      }
    },

    onActivationStatusChanged: (payload) => {
      if (botConfig.updateFromWebSocket && payload) {
        botConfig.updateFromWebSocket(payload);
      }
    },

    onCompletenessScoreUpdated: (payload) => {
      if (botConfig.updateFromWebSocket && payload) {
        botConfig.updateFromWebSocket(payload);
      }
    },

    onValidationStatusChanged: (payload) => {
      if (botConfig.updateFromWebSocket && payload) {
        botConfig.updateFromWebSocket(payload);
      }
    },

    onConfigurationReceived: (payload) => {
      if (payload && botConfig.updateFromWebSocket) {
        botConfig.updateFromWebSocket(payload);
      }
    },

    onError: (error) => {
      console.error('WebSocket error:', error);
    }
  }), [forceRefresh, botConfig]);

  // Use centralized hook to connect to Bot Configuration channel
  // Only use WebSocket if partnerId is available
  const { isSubscribed, isConnected } = useBotConfigurationChannel(partnerId, assistantId, botChannelHandlers);

  const value = {
    ...botConfig,
    forceRefresh,
    lastUpdate,
    isWebSocketConnected: partnerId ? (isConnected && isSubscribed) : false
  };

  return (
    <BotConfigurationContext.Provider value={value}>
      {children}
    </BotConfigurationContext.Provider>
  );
};